<template>
  <div class="flex flex-col gap-5">
    <div>
      <div class="flex items-baseline justify-end">
        <a class="mt-2">
          <BDropdown
            data-test="lead-dropdown"
            position="is-bottom-left"
          >
            <template #trigger>
              <BIcon
                class="flex-center"
                icon="ellipsis-vertical"
                pack="fas"
                size="is-small"
              />
            </template>
            <BDropdownItem
              data-test="lead-delete"
              :disabled="current_profile.attributes.role === Employee.ROLE_MAINTAINER"
              @click="deleteLead"
            >
              {{ $t('contact.actions.deleteLead') }}
            </BDropdownItem>
          </BDropdown>
        </a>
      </div>
      <LeadForm
        action="edit"
        :disabled="!isEditable"
        :lead_prop="lead"
        @setLead="updateLead"
      >
        <template #lead-form-header="{ submit, reset, isVisible }">
          <div
            v-if="isVisible"
            class="buttons"
          >
            <ButtonPrimary
              action="save"
              data-test="lead-form-save-edit"
              @onClick="submit"
            >
              {{ $t('button.actions.save_changes') }}
            </ButtonPrimary>
            <ButtonTertiary @onClick="reset" />
          </div>
        </template>
        <template #lead-form-footer="{ submit, reset, isVisible }">
          <div
            v-if="isVisible"
            class="buttons"
          >
            <ButtonPrimary
              action="save"
              @onClick="submit"
            >
              {{ $t('button.actions.save_changes') }}
            </ButtonPrimary>
            <ButtonTertiary @onClick="reset" />
          </div>
        </template>
      </LeadForm>
    </div>
    <div>
      <p class="title is-5">
        {{ $t('customer.labels.units') }}
      </p>
      <hr />
      <p
        v-if="units_lead.length === 0"
        class="subtitle is-6"
      >
        {{ $t('sales.labels.not_shown_interest') }}
      </p>
      <div
        v-for="unit in units_lead"
        :key="unit.id"
        class="flex items-center py-4"
      >
        <div class="grow truncate">
          <h5 class="title is-5 truncate">
            <AppLink
              :to="{
                name: ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS_INFORMATION,
                params: { project_id: unit.project.id, level_type: 'Unit', level_id: unit.id },
              }"
            >
              {{ unit.name }}
            </AppLink>
          </h5>
          <h6 class="subtitle is-6 truncate">
            {{ unit.project.name }}
          </h6>
        </div>
        <div style="min-width: 140px">
          <DealStatus
            class="ml-3"
            :deal="unit.deal"
            :unit="unit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LeadForm from '@/app/_employees/components/contacts/LeadForm.vue';
import { Employee } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { mapActions, mapGetters } from 'vuex';
import DealStatus from '@/app/shared_components/sales/DealStatus.vue';
import useGlobalConfirm from '@/composables/useGlobalConfirm';

export default {
  name: 'LeadProfile',
  components: {
    LeadForm,
    DealStatus,
  },

  props: {
    lead: { type: Object, default: Object },
    projects: { type: Object, default: null },
  },

  setup() {
    const { confirm } = useGlobalConfirm();
    return { confirm };
  },

  data() {
    return {
      is_loading: false,
      Employee,
      ROUTES_EMPLOYEES,
    };
  },

  computed: {
    ...mapGetters(['current_profile', 'group_leads', 'lead_groups', 'deals', 'units']),
    isEditable() {
      return this.current_profile.attributes.role === Employee.ROLE_OWNER;
    },

    units_lead() {
      const group_leads = _.filter(this.group_leads, (gc) => gc.attributes.lead_id === this.lead.id);
      const lead_groups = _.filter(this.lead_groups, (lg) =>
        _.map(group_leads, 'attributes.lead_group_id').includes(lg.id),
      );
      const deals = _.filter(this.deals, (d) => _.map(lead_groups, 'id').includes(d.attributes.lead_group_id));

      const units = _.filter(this.units, (u) => _.map(deals, 'attributes.unit_id').includes(u.id));

      return _.map(units, (u) => {
        const project = _.find(this.projects, ['id', u.attributes.project_id]);
        return {
          name: u.attributes.name,
          id: u.id,
          deal: _.find(deals, ['attributes.unit_id', u.id]),
          project: {
            name: project.attributes.name,
            id: project.id,
          },
        };
      });
    },
  },

  methods: {
    ...mapActions(['UPDATE_LEAD', 'DELETE_LEAD']),

    async updateLead(lead_old) {
      this.is_loading = true;
      try {
        const lead = await this.UPDATE_LEAD({ record: lead_old });
        this.toast_update_succeeded(lead.attributes.name);
      } catch (error) {
        this.toast_action_failed(error);
      } finally {
        this.is_loading = false;
      }
    },

    async deleteLead() {
      const confirmed = await this.confirm({
        destructive: true,
        header: this.$t('modal.labels.deleteLeadTitle', { lead: this.lead.attributes.name }),
        message: this.$t('modal.labels.deleteLeadMessage'),
      });
      if (!confirmed) return;

      try {
        this.lead.attributes.discarded_at = new Date();
        await this.DELETE_LEAD({ record: this.lead });
        this.$emit('deleted', this.lead);
      } catch (error) {
        this.toast_action_failed(error);
      }
    },
  },
};
</script>
